import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { setClassNames } from '@ddc/classnames-js';
import FragmentContent from './FragmentContent';
import useDeviceType from '../hooks/useDeviceType';
import useTrackEvent from '../hooks/useTrackEvent';
import { useDrawer } from '../context/DrawerContext';
import { useScrollContainer } from '../context/ScrollContainerContext';
import { resolveFragmentPresentation } from '../utils/fragmentPresentation';
import { SHARED_FRAGMENT_MARKER } from '../global-constants';
import {
	DRAWER_EVENT,
	ZONE_POSITION,
	INTERACTION_TYPE,
	TRACKING_CONFIG,
	WIDGET_STATE,
	resolveFragmentType
} from '../utils/trackEvent';

/**
 * Maps a CSS class detected in the fragment HTML to class-list mutations
 * applied to the fragment container.
 *
 *   add             — classes always appended to the container
 *   exclude         — base classes to remove on all device types
 *   excludeOnMobile — base classes to remove when deviceType is 'MOBILE'
 *
 * Adding a new fragment type only requires a new entry here — no component
 * logic changes needed (Open/Closed).
 *
 * @type {Record<string, FragmentClassConfig>}
 */
const FRAGMENT_CLASS_MAP = {
	'hours-default': {
		add: ['py-4', 'ddc-font-size-small'],
		excludeOnMobile: ['px-4']
	},
	// ws-site-text-search's panel-mode content wrapper (search results panel,
	// T17) sets its own horizontal inset via --sts-panel-content-padding-x,
	// mapped in _fragment.scss to the drawer's own outer/inner padding
	// tokens — px-4 would double that padding. 'sts-panel-content' only
	// appears in the search widget's panel-mode markup, never in its other
	// four display modes, so this can't affect other fragment types.
	'sts-panel-content': {
		exclude: ['px-4']
	},
	// ws-model-selector's row card (cardLayout=row) sets its own horizontal
	// inset via the drawer-scoped --drawer-horiz-pad-inner rule in
	// _fragment.scss — px-4 would double that padding. 'ms-row-card' only
	// appears in the row layout's markup (Row.jsx/Responsive.jsx), never in
	// stacked/GridCard, so this can't affect other fragment types.
	'ms-row-card': {
		exclude: ['px-4']
	},
	// ws-group-logos ships its own built-in horizontal padding 8px (ddc-content).
	// Design wants 16px padding on every side, so we top that up with
	// px-3 (+8px horizontal) and py-4 (16px vertical) to land on 16px all
	// around — px-4 doesn't fit that scheme, so it's excluded on mobile too.
	'ws-group-logos': {
		add: ['py-4', 'px-3'],
		excludeOnMobile: ['px-4']
	}
};

// SHARED_FRAGMENT_MARKER is the shared marker MegaMenuFragment also applies —
// every fragment-scoped rule in _fragment.scss keys off it (hover/active, padding,
// the <li> suppression block). Keep both classes so those rules reach the drawer too.
const BASE_CLASSES = [
	SHARED_FRAGMENT_MARKER,
	'ws-nav-drawer__fragment-content',
	'px-4'
];

/**
 * Ordered CSS-selector probes that classify what the user activated inside a
 * content zone, for the `interactionType` attribute (CR-6).
 *
 * Order is the specification, not an optimization. A phone number is an anchor
 * and a search submit is a button, so the narrower meaning has to win first or
 * every tel: link would be recorded as a generic link. Anything unmatched is
 * deliberately not tracked — see the handler.
 *
 * @type {Array<[string, string]>}
 */
const INTERACTION_SELECTORS = [
	[INTERACTION_TYPE.PHONE, 'a[href^="tel:"]'],
	// `.search-form` and `.btn` below are the only non-semantic probes here, and
	// they carry a known cost: a fragment template that applies either class for
	// layout rather than meaning will have its clicks misclassified. Both are kept
	// because the CMS fragment set in FRAGMENT_CLASS_MAP does use them as real
	// control markers today and dropping them would silently under-report. If that
	// set grows, prefer adding semantic probes over more class names.
	[
		INTERACTION_TYPE.SEARCH,
		'[role="search"], input[type="search"], .search-form'
	],
	[
		INTERACTION_TYPE.CTA,
		'button, [role="button"], input[type="submit"], .btn'
	],
	[INTERACTION_TYPE.LINK, 'a[href]']
];

/**
 * Classifies a clicked node by walking up to the nearest recognized control.
 *
 * The walk is bounded to `container`, because `closest` otherwise climbs to the
 * document root: an ancestor outside the zone carrying one of the non-semantic
 * probes below (a `.btn` layout wrapper on the host page, say) would classify a
 * click on inert fragment content as a CTA. The classification should depend only
 * on the fragment markup it describes.
 *
 * @param {EventTarget} target - the click's target
 * @param {Element} [container] - the content zone; unbounded when absent
 * @returns {string|null} an INTERACTION_TYPE, or null when nothing matched
 */
const resolveInteractionType = (target, container) => {
	if (!target || typeof target.closest !== 'function') return null;
	const match = INTERACTION_SELECTORS.find(([, selector]) => {
		const control = target.closest(selector);
		return control && (!container || container.contains(control));
	});
	return match ? match[0] : null;
};

/**
 * Resolves the final class list for a loaded fragment by applying
 * FRAGMENT_CLASS_MAP entries matched against the HTML content.
 *
 * @param {string|null} html - sanitized fragment HTML
 * @param {string|undefined} deviceType - 'MOBILE' | 'DESKTOP'
 * @returns {string[]}
 */
const resolveFragmentClasses = (html, deviceType) => {
	const isMobile = deviceType === 'MOBILE';
	const matched = Object.entries(FRAGMENT_CLASS_MAP).filter(([detectClass]) =>
		html?.includes(detectClass)
	);
	const collect = (key) => matched.flatMap(([, config]) => config[key] || []);

	const add = collect('add');
	const exclude = new Set([
		...collect('exclude'),
		...(isMobile ? collect('excludeOnMobile') : [])
	]);

	const base = BASE_CLASSES.filter((c) => !exclude.has(c));
	return [...base, ...add];
};

/**
 * DrawerNavFragment — thin wrapper over FragmentContent that supplies the
 * drawer's real scroll-container observer root and its deviceType-aware
 * class-list policy. Lazy-fetches a fragment URL and renders the HTML inline,
 * showing a skeleton while loading and collapsing silently on error.
 *
 * This component only fetches its content once it's about to become
 * visible (see FragmentContent/useFragmentLoader for the "fetch on scroll
 * into view" mechanism). To know when that happens, something has to watch
 * the right scrollable box — and which box that is depends on where this
 * gets rendered:
 *
 * - Inside a drilldown panel, each panel has its own internal scroll area,
 *   so this reads that panel's own box from the nearest
 *   ScrollContainerContext (each keep-mounted drilldown panel provides its
 *   own — see that context for why one shared box wouldn't work here).
 * - Mounted somewhere with no such context above it at all — e.g. eagerly in
 *   the persistent header row, instead of lazily inside an opened drilldown
 *   panel — there's no scrollable panel to watch, so it falls back to
 *   watching the whole browser viewport instead (ScrollContainerContext's
 *   VIEWPORT_FALLBACK). That's already the right behavior, with no extra
 *   code needed: a header-row icon is on screen the moment the page loads,
 *   so the fetch fires immediately, exactly as if it had "scrolled into
 *   view." (Full investigation: US2059853 T39, ralph-cc/progress.txt.)
 *
 * @param {DrawerNavFragmentProps} props
 */
const DrawerNavFragment = ({
	url,
	skeletonHeight = '12rem',
	headless = false,
	zonePosition = ZONE_POSITION.BELOW
}) => {
	const scrollContainerRef = useScrollContainer();
	const deviceType = useDeviceType();
	const track = useTrackEvent();
	const { isOpen } = useDrawer();

	// Derived from the URL, not from the clicked DOM — see resolveFragmentType.
	// Hoisted out of the handler because it depends only on the prop.
	const fragmentType = resolveFragmentType(url);

	// Delegated rather than bound per element: fragment markup is fetched and
	// injected, so there are no React elements to attach handlers to, and the
	// content can change under us on re-init. One listener on the container
	// survives all of that and adds no network request of its own (CR-8).
	const handleZoneInteraction = useCallback(
		(event) => {
			// The keep-mounted panels leave closed fragments in the DOM; without
			// this a stray click on a hidden zone would still report.
			if (!isOpen) return;

			const interactionType = resolveInteractionType(
				event.target,
				event.currentTarget
			);
			// Clicks on inert parts of a fragment (headings, plain text, layout
			// wrappers) are not interactions worth an event.
			if (!interactionType) return;

			track(DRAWER_EVENT.CONTENT_ZONE_INTERACTION, {
				// Interaction type is in `element` as well as its own attribute so
				// the phone/search/cta/link split survives the GA path, which keeps
				// only element/action/result. See TRACKING_CONFIG.
				element: TRACKING_CONFIG.getContentZoneEl(
					fragmentType,
					interactionType
				),
				result: TRACKING_CONFIG.contentZoneResult,
				widgetState: WIDGET_STATE.DRAWER_OPEN,
				zonePosition,
				fragmentType,
				interactionType
			});
		},
		[isOpen, track, zonePosition, fragmentType]
	);

	const resolveClassName = ({ status, html }) => {
		const { isLoaded, isPending, isVisible } = resolveFragmentPresentation(
			status,
			headless
		);
		if (!isVisible) {
			// A headless mount (e.g. the header row's My Cars icon) never shows
			// inline content -- it only exists to fetch and hydrate a widget that
			// renders its own hidden UI elsewhere (a modal portaled to
			// document.body). So none of the classes for visible content apply
			// here: no loading modifier, no FRAGMENT_CLASS_MAP entries, no px-4.
			//
			// ws-nav-drawer__fragment--headless (see _fragment.scss for exactly
			// what it does and why) is what keeps this container out of the
			// header row's layout and visually hidden, while still giving it a
			// real, measurable box so our lazy-load fetch trigger can detect it.
			// That's safe because a React portal rendered inside this container
			// (the modal) shows up independent of this ancestor's CSS.
			return setClassNames([
				SHARED_FRAGMENT_MARKER,
				'ws-nav-drawer__fragment--headless'
			]);
		}
		const resolvedClasses = isLoaded
			? resolveFragmentClasses(html, deviceType)
			: BASE_CLASSES;
		return setClassNames([
			...resolvedClasses,
			isPending && 'ws-nav-drawer__fragment-content--loading'
		]);
	};

	return (
		<FragmentContent
			url={url}
			root={scrollContainerRef}
			classNameResolver={resolveClassName}
			skeletonHeight={skeletonHeight}
			onClick={handleZoneInteraction}
			// Every drawer fragment (drilldown + submenu) flows through here, so
			// this is the single place that marks the drawer surface. An embedded
			// ws-rec-vehicles reads it (containerRef.closest) for its 2-column
			// layout. The mega menu uses MegaMenuFragment, which does not set it.
			recVehiclesContext="nav-drawer"
			// Drilldown bodies are keep-mounted (they toggle display, never
			// unmount), so re-initializing on every reshow would leak DOM nodes +
			// listeners per reopen. Opt out and init once, matching MegaMenuFragment.
			// The one-time mount re-init (for a url already cached by the mega menu)
			// still runs to scope this instance's widget ids — so a dual-embedded
			// widget's bundle runs at most twice/page (≤1 "invoked multiple times"
			// warning). That's bounded, not the leak returning — don't re-enable
			// reshow re-init to "fix" it.
			reinitOnReshow={false}
			headless={headless}
		/>
	);
};

DrawerNavFragment.propTypes = {
	url: PropTypes.string,
	skeletonHeight: PropTypes.string,
	headless: PropTypes.bool,
	zonePosition: PropTypes.oneOf(Object.values(ZONE_POSITION))
};

export default DrawerNavFragment;
